import { forwardRef, useMemo, type ForwardedRef } from "react";

import {
  MediaGallery,
  type MediaGalleryHandle,
  type MediaGalleryItem,
} from "@rubi/ui";
import { useTranslation } from "react-i18next";

import { photoLetter } from "@/components/alerts/entities/entityPhotos";
import { BulletinEntityResource } from "@/components/alerts/entities/entityRows";
import { mediaEntityCaption } from "@/components/alerts/entities/mediaEntityCaption";
import { IEvidenceFile } from "@/types/typesAlerts";

/**
 * A published bulletin's photos and videos.
 *
 * Everything about how they are shown belongs to `MediaGallery` in `@rubi/ui`, which the bulletin
 * board's cards render too. What is here is the mapping into it: which url is the hero and which is
 * the thumbnail, and what a photo is captioned with.
 */

/** Hero plus strip: 320 of photo where the panel is wide, 240 where it is narrow. */
const GALLERY_HEIGHT = { xs: 312, sm: 392 };
/** Photos on the hero at once, so consecutive ones are seen together. */
const CLUSTER = 3;

interface AlertMediaSliderProps {
  medias: IEvidenceFile[];
  /**
   * Omitted where the viewer is not entitled to them — the public bulletin page has no entities on
   * its resource, and shows no caption.
   */
  entities?: BulletinEntityResource[];
  /** What a click on the hero means. The detail views open the expanded viewer on that photo. */
  handleSlideClick: (index: number) => void;
}

const isVideo = (media: IEvidenceFile) => media.content_type.includes("video");

/** Module-level, so an omitted `entities` is the same reference on every render — a default
 *  `[]` in the parameter list is a new array each time, which is a `useMemo` that never holds. */
const NO_ENTITIES: BulletinEntityResource[] = [];

function AlertMediaSlider(
  { medias, entities = NO_ENTITIES, handleSlideClick }: AlertMediaSliderProps,
  handle: ForwardedRef<MediaGalleryHandle>,
) {
  const { t } = useTranslation("bulletin/entities");

  const media = useMemo<MediaGalleryItem[]>(
    () =>
      medias.map((file, index) =>
        isVideo(file)
          ? {
              type: "video",
              src: file.download_url,
              label: photoLetter(index),
            }
          : {
              type: "image",
              src: file.thumbnail_url,
              // The letter the entity list points at this photo by.
              label: photoLetter(index),
              // The hero is worth the original: unlike the card's, it is large enough that the
              // 400px derivative would show, and only the one on screen is ever loaded.
              heroSrc: file.download_url,
              // Thumbnails are generated async; fall back to the original.
              fallbackSrc: file.download_url,
              // Dense where the hero holds a cluster: each photo is then about a third of the panel.
              caption: (dense: boolean) =>
                mediaEntityCaption(file, entities, t, { dense }),
            },
      ),
    [medias, entities, t],
  );

  if (medias.length === 0) return null;

  return (
    <MediaGallery
      ref={handle}
      media={media}
      height={GALLERY_HEIGHT}
      cluster={CLUSTER}
      slidesPerView={7}
      onHeroClick={handleSlideClick}
    />
  );
}

export default forwardRef(AlertMediaSlider);
